import React from "react";
import { Alert } from "reactstrap";
import * as Sentry from "@sentry/browser";

/**
 * FormErrorBoundary - Specialized error boundary for forms
 * Displays inline error messages without breaking the entire form
 * Preserves form data and allows users to fix issues and retry
 *
 * @component
 * @example
 * <FormErrorBoundary>
 *   <YourFormComponent />
 * </FormErrorBoundary>
 */
class FormErrorBoundary extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			hasError: false,
			error: null,
			errorCount: 0,
		};
	}

	static getDerivedStateFromError(error) {
		return { hasError: true };
	}

	componentDidCatch(error, errorInfo) {
		// Log error details
		if (process.env.NODE_ENV === "development") {
			console.error("FormErrorBoundary caught an error:", error, errorInfo);
		}

		// Log to Sentry with form context
		Sentry.captureException(error, {
			contexts: {
				react: {
					componentStack: errorInfo.componentStack,
				},
			},
			tags: {
				errorBoundary: "form",
			},
		});

		this.setState((prevState) => ({
			error,
			errorCount: prevState.errorCount + 1,
		}));
	}

	handleDismiss = () => {
		this.setState({
			hasError: false,
			error: null,
		});
	};

	render() {
		if (this.state.hasError) {
			const { onError } = this.props;

			// Call optional error callback
			if (onError) {
				onError(this.state.error);
			}

			// Show inline error message that can be dismissed
			return (
				<div className="form-error-boundary">
					<Alert color="danger" isOpen={true} toggle={this.handleDismiss}>
						<h5 className="alert-heading">
							<i className="nc-icon nc-alert-circle-i mr-2"></i>
							Unable to Process Form
						</h5>
						<p className="mb-2">
							An error occurred while processing your form. Your data has been
							preserved. Please check your inputs and try again.
						</p>
						{this.state.error && (
							<small className="text-muted d-block mt-2">
								Error: {this.state.error.message || "Unknown error"}
							</small>
						)}
					</Alert>
					{/* Still render children so form data is preserved */}
					{this.props.children}
				</div>
			);
		}

		return this.props.children;
	}
}

export default FormErrorBoundary;
